'use client'

import React, { createContext, useEffect } from 'react'
import '../theme.scss'
import { STORAGE_KEYS } from 'shared/utils/storage'
import useStorage from 'shared/hooks/useStorage'
import { isValidTheme, ThemeOption } from '../theme.helper'

type ProviderType = {
  theme: ThemeOption
  changeTheme: (theme: ThemeOption) => void
}

const initialValues: ProviderType = {
  theme: 'default',
  changeTheme: () => null,
}

const ThemeContext = createContext<ProviderType>(initialValues)

interface ThemeProviderProps {
  children: React.ReactNode
  theme?: ThemeOption
  storage?: Storage
}

const ThemeProvider = ({
  children,
  theme: passedTheme,
  storage,
}: ThemeProviderProps) => {
  const [theme, setTheme] = useStorage(
    STORAGE_KEYS.theme,
    isValidTheme(passedTheme) ? passedTheme : 'default',
    storage,
  )

  const changeTheme = (newTheme: ThemeOption) => {
    setTheme(newTheme)
  }

  useEffect(() => {
    // Remove any existing theme classes
    document.body.classList.remove('theme-default', 'theme-dark', 'theme-light')
    // Add the current theme class to the body
    document.body.classList.add(`theme-${theme}`)
  }, [theme])

  return (
    <ThemeContext.Provider value={{ theme, changeTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

function useTheme() {
  const context = React.useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

export { ThemeProvider, useTheme }
